"use client";

import React, { ReactElement } from "react";
import Link, { LinkProps } from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useSession } from "@/contexts/session";
import { buildLoginHref } from "@/components/utilities/authentication/useRedirectToLogin";

function LoginLink({
	href,
	...props
}: Omit<LinkProps, "href" | "children"> & {
	children: React.ReactNode;
	href?: string;
}) {
	const returnUrl = usePathname();
	return <Link href={href || buildLoginHref(returnUrl)} {...props} />;
}

export function LoginOrCreateAccountIfUnauthenticated({
	action = "login",
	children,
}: {
	action?: "login" | "create-account";
	children: React.ReactNode;
}) {
	const returnUrl = usePathname();
	const router = useRouter();
	const { hasAuthenticatedSession } = useSession();

	if (hasAuthenticatedSession) {
		return <>{children}</>;
	}

	function handleClick(e: React.MouseEvent) {
		e.preventDefault();
		e.stopPropagation();
		if (action === "login") {
			router.push(buildLoginHref(returnUrl));
		} else {
			router.push(`/create-account?returnUrl=${encodeURIComponent(returnUrl)}`);
		}
	}

	return (
		<>
			{React.Children.map(children, (child) => {
				if (!React.isValidElement(child)) {
					return child;
				}

				return React.cloneElement(
					child as ReactElement<{ onClick?: React.MouseEventHandler }>,
					{
						onClick: (event: React.MouseEvent) => {
							handleClick(event);
						},
					},
				);
			})}
		</>
	);
}

export default LoginLink;
